import { useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AddOnTextInput, Button, DropdownSelect, Dropzone, Icon, Spinner, TextInput, Toggle, Tooltip, Typography } from "@/components/atoms";
import { useGetVehicleTypesQuery } from "@/redux/apis/config/trip/vehicleType/vehicleTypeApi";
import { useAddAvatarMutation, useGetVehicleFormOptionsQuery, useUpdateVehicleMutation } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast, getAssetUrl } from "@/utils";
import { step1Schema, Step1FormData } from "../schemas";
import { VehicleTypeMultiSelect } from "../../VehicleTypeMultiSelect";
import { getInitialVehicleTypeUuids } from "../../helpers";
import AvatarPlaceholder from "@/assets/images/vehicle/avatar-placeholder.svg";

export type { Step1FormData } from "../schemas";

interface Step1VehicleInfoProps {
  vehicleId: string;
  vehicle: Vehicle;
  onNext: () => Promise<void>;
  onFinishLater: () => void;
}

export const Step1VehicleInfo = ({ vehicleId, vehicle, onNext, onFinishLater }: Step1VehicleInfoProps) => {
  const [updateVehicle, { isLoading: isSaving }] = useUpdateVehicleMutation();
  const [addAvatar, { isLoading: isUploadingAvatar }] = useAddAvatarMutation();
  const { data: options, isFetching: isFetchingOptions } = useGetVehicleFormOptionsQuery();
  // Catalog on/off status per type (options endpoint carries only uuid+name).
  const { data: typeStatus } = useGetVehicleTypesQuery({ page: "1", page_size: "200" }, { refetchOnMountOrArgChange: true });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    vehicle.avatar ? getAssetUrl(vehicle.avatar, { height: 200, width: 200, background: "transparent" }) : null
  );

  const vehicleTypes = useMemo(() => {
    if (!options?.vehicleTypes) return [];
    const activeByUuid = new Map(typeStatus?.vehicleTypes.map((t) => [t.uuid, t.isActive]));
    return options.vehicleTypes.map((vt) => ({ name: vt.name, value: vt.uuid, isActive: activeByUuid.get(vt.uuid) }));
  }, [options?.vehicleTypes, typeStatus?.vehicleTypes]);

  const form = useForm<Step1FormData>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      avatar: null,
      hasExistingAvatar: !!vehicle.avatar,
      hasMarkings: vehicle.hasMarkings ?? false,
      vehicleTypeUuids: getInitialVehicleTypeUuids(vehicle),
      nickname: vehicle.identifier || "",
      capacity: vehicle.capacity.pax,
      bags: vehicle.capacity.bags,
      maintenanceFee: vehicle.maintenanceFee || 0,
    },
  });

  const { register, control, handleSubmit, formState: { errors }, setValue } = form;
  const maxCapacity = vehicle.model.maxCapacity;
  const isLoading = isSaving || isUploadingAvatar;

  const handleUpload = (file: File) => {
    setValue("avatar", file, { shouldDirty: true, shouldValidate: true });
    setValue("hasExistingAvatar", true);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const onSubmit = async (data: Step1FormData) => {
    try {
      // Upload avatar if provided
      if (data.avatar) {
        const avatarForm = new FormData();
        avatarForm.append("avatar", data.avatar);
        await addAvatar({ id: vehicleId, body: avatarForm }).unwrap();
      }

      // Update vehicle details + advance setup_step only if on this step for the first time
      await updateVehicle({
        id: vehicleId,
        data: {
          identifier: data.nickname || "",
          has_markings: data.hasMarkings,
          capacity_pax: data.capacity,
          capacity_bags: data.bags,
          maintenance_fee: data.maintenanceFee,
          vehicle_types: data.vehicleTypeUuids.map((uuid) => ({ uuid })),
          ...(vehicle.setupStep === 1 && { setup_step: 2 }),
          documents: vehicle.documents.map((d) => ({ uuid: d.uuid, expiry_date: d.expiryDate, document_type: d.documentType })),
        },
      }).unwrap();

      await onNext();
    } catch (e: unknown) {
      getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div>
          <Dropzone
            accept={{
              "image/png": [".png"],
              "image/jpeg": [".jpeg", ".jpg"],
            }}
            maxSize={10000000}
            upload={handleUpload}
            className="w-full border-transparent p-5 transition-colors hover:border-neutral-dark-gray"
            detail={
              <div className="relative flex flex-col items-start gap-8 md:flex-row md:items-center">
                <img
                  src={avatarPreview ?? AvatarPlaceholder}
                  alt="Vehicle Image"
                  className="h-[100px] w-[100px] grow-0 rounded-full object-contain"
                />
                <div className="flex flex-1 flex-col">
                  <Typography variant="action" className="text-black">
                    Vehicle Image
                  </Typography>
                  <Typography className="text-neutral-dark-gray mt-2.5">
                    Upload a vehicle image (PNG and JPG supported), we recommend a front view with the Number Plate on it
                  </Typography>
                  <Button type="button" variant="custom" size="sm" className="mt-0.5 -translate-x-3 self-start text-info">
                    Upload
                  </Button>
                </div>
              </div>
            }
          />
          {errors.avatar && (
            <Typography className="mt-1 text-sm text-danger">{errors.avatar.message}</Typography>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Typography>Vehicle has any markings?</Typography>
          <Controller
            control={control}
            name="hasMarkings"
            render={({ field }) => <Toggle size="sm" checked={field.value} onChange={field.onChange} />}
          />
        </div>

        <div className="grid grid-cols-2 gap-5 gap-y-6">
          <label htmlFor="vehicleTypeUuids" className="col-span-2 xl:col-span-1">
            <Typography className="relative flex items-center gap-2 leading-[2rem]">
              Vehicle Type
              <Tooltip content="The type classification of your vehicle" placement="top">
                <Icon name="InfoCircle" variant="Bold" size="md" className="relative z-10 rotate-180 text-neutral-dark-gray" />
              </Tooltip>
            </Typography>
            <Controller
              control={control}
              name="vehicleTypeUuids"
              render={({ field }) => (
                <VehicleTypeMultiSelect
                  options={vehicleTypes}
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!errors.vehicleTypeUuids}
                  disabled={isFetchingOptions}
                />
              )}
            />
            {errors.vehicleTypeUuids && (
              <Typography className="mt-1 text-sm text-danger">{errors.vehicleTypeUuids.message}</Typography>
            )}
          </label>

          <label htmlFor="nickname" className="col-span-2 xl:col-span-1">
            <Typography className="leading-[2rem]">Vehicle Nickname (Optional)</Typography>
            <TextInput
              {...register("nickname")}
              id="nickname"
              placeholder="Enter Vehicle Nickname"
              className="[&>div]:w-full [&_input]:w-full"
            />
          </label>

          <label htmlFor="capacity" className="col-span-2 xl:col-span-1">
            <Typography className="leading-[2rem]">Passenger Capacity</Typography>
            <Controller
              control={control}
              name="capacity"
              render={({ field }) => (
                <DropdownSelect
                  className="w-full"
                  placeholder="Select Passenger Capacity"
                  options={Array.from({ length: maxCapacity }, (_, i) => (i + 1).toString())}
                  value={field.value?.toString()}
                  onChange={(value: string) => field.onChange(+value)}
                />
              )}
            />
          </label>

          <label htmlFor="bags" className="col-span-2 xl:col-span-1">
            <Typography className="leading-[2rem]">Bag Capacity</Typography>
            <Controller
              control={control}
              name="bags"
              render={({ field }) => (
                <DropdownSelect
                  className="w-full"
                  placeholder="Select Number of Capacity"
                  options={Array.from({ length: 101 }, (_, i) => i.toString())}
                  value={field.value?.toString()}
                  onChange={(value: string) => field.onChange(+value)}
                />
              )}
            />
          </label>

          <label htmlFor="maintenanceFee" className="col-span-2 xl:col-span-1">
            <Typography className="leading-[2rem]">Vehicle Owner Maintenance Fee (Optional)</Typography>
            <AddOnTextInput
              {...register("maintenanceFee", { valueAsNumber: true })}
              id="maintenanceFee"
              type="number"
              endAddOn="%"
              min={0}
              max={100}
              placeholder="0"
              className="w-full [&>div]:flex-1"
            />
            <Typography variant="paragraph" className="mt-1 text-neutral-silver-gray">
              Portion of the driver payment paid directly to the vehicle owner
            </Typography>
          </label>
        </div>

        <div className="mt-4 flex flex-col-reverse gap-3 xl:flex-row xl:justify-between xl:gap-4">
          <Button type="button" variant="custom" size="xs" className="text-neutral-silver-gray underline xl:w-auto" onClick={onFinishLater}>
            Finish later
          </Button>
          <Button type="submit" variant="primary" size="lg" className="w-full xl:w-auto" endIcon="ArrowRight" disabled={isLoading}>
            {isLoading ? <Spinner /> : "Next"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
